// Screenplay editor constants — page measurements, element styles, tag types,
// quick-insert definitions, and autocomplete regexes.
// Extracted from SimpleScreenplayViewer.js for maintainability.

import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PersonIcon from '@mui/icons-material/Person';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EastIcon from '@mui/icons-material/East';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';

// ─── Page Measurements ───────────────────────────────────────────────────────

// Standard US Letter page measurements (in inches)
// Per Hollywood/industry standard: 1.5" left, 1" right, 1" top, 1" bottom
export const MEASUREMENTS = {
  // Page dimensions
  PAGE_WIDTH: 8.5,
  PAGE_HEIGHT: 11,

  // Standard margins (industry standard)
  LEFT_MARGIN: 1.5,    // Left margin always 1.5"
  RIGHT_MARGIN: 1.0,   // Right margin 1.0"
  TOP_MARGIN: 1.0,     // Top margin 1.0"
  BOTTOM_MARGIN: 1.0,  // Bottom margin 1.0"

  // Element measurements from left margin
  CHARACTER_INDENT: 2.2,  // Character names start 3.7" from left edge (1.5 + 2.2)
  PARENTHETICAL_INDENT: 1.6,  // Parentheticals start 3.1" from left edge (1.5 + 1.6)
  DIALOGUE_INDENT: 1,     // Dialogue starts 2.5" from left edge (1.5 + 1)
  DIALOGUE_WIDTH: 3.5,    // Dialogue extends 3.5" (wraps at 6" from left edge)

  // Page number position (from top edge of page)
  PAGE_NUMBER_TOP: 0.5,   // Page numbers at 0.5" from top (industry standard)
};

// Derived content width inside page (inches)
export const CONTENT_WIDTH = MEASUREMENTS.PAGE_WIDTH - MEASUREMENTS.LEFT_MARGIN - MEASUREMENTS.RIGHT_MARGIN;
// Dialogue right margin so visual width equals DIALOGUE_WIDTH
export const DIALOGUE_RIGHT_MARGIN = CONTENT_WIDTH - MEASUREMENTS.DIALOGUE_INDENT - MEASUREMENTS.DIALOGUE_WIDTH;

// ─── Element Styles ───────────────────────────────────────────────────────────

export const baseStyles = {
  fontFamily: 'Courier Prime, Courier, monospace',
  fontSize: '12pt',
  lineHeight: '1.19',
  whiteSpace: 'pre-wrap',
  margin: 0,
  padding: 0
};

export const elementStyles = {
  scene: {
    ...baseStyles,
    textTransform: 'uppercase'
  },
  action: {
    ...baseStyles
  },
  character: {
    ...baseStyles,
    textTransform: 'uppercase',
    marginLeft: `${MEASUREMENTS.CHARACTER_INDENT}in`
  },
  parenthetical: {
    ...baseStyles,
    marginLeft: `${MEASUREMENTS.PARENTHETICAL_INDENT}in`,
    marginRight: `${MEASUREMENTS.PARENTHETICAL_INDENT}in`
  },
  dialogue: {
    ...baseStyles,
    marginLeft: `${MEASUREMENTS.DIALOGUE_INDENT}in`,
    marginRight: `${DIALOGUE_RIGHT_MARGIN}in`
  },
  transition: {
    ...baseStyles,
    textAlign: 'right',
    marginRight: 0,
    textTransform: 'uppercase'
  },
  centered: {
    ...baseStyles,
    textAlign: 'center'
  },
  dualDialogue: {
    ...baseStyles,
    display: 'flex',
    justifyContent: 'space-between'
  },
  blank: {
    ...baseStyles,
    height: '14.28pt'
  },
  lyric: {
    ...baseStyles,
    fontStyle: 'italic',
    marginLeft: `${MEASUREMENTS.DIALOGUE_INDENT}in`,
    marginRight: `${DIALOGUE_RIGHT_MARGIN}in`
  },
  note: {
    ...baseStyles,
    color: '#666',
    fontStyle: 'italic'
  }
};

// Dark mode colors
export const darkModeColors = {
  background: '#050505',
  text: '#f5f5f5',
  element: '#ffffff'
};

// Dark mode element styles
// Build dark mode styles by adding white color to each element style,
// except for elements that have their own semantic color (e.g. note = grey)
export const darkModeElementStyles = Object.fromEntries(
  Object.entries(elementStyles).map(([key, style]) => [
    key,
    { ...style, color: key === 'note' ? '#999' : (style.color || darkModeColors.element) }
  ])
);

// ─── Tag Types ─────────────────────────────────────────────────────────────────
// Aligned with breakdown categories. Exported for use in other components.

export const TAG_TYPES = {
  // Breakdown categories
  CHARACTER: { label: 'Character', color: '#E57373' },  // Combined CHARACTER and CAST into one
  STUNTS: { label: 'Stunts', color: '#FFB74D' },
  EXTRAS: { label: 'Extras/Atmosphere', color: '#81C784' },
  EXTRAS_SILENT: { label: 'Extras/Silent', color: '#FFF176' },
  PROPS: { label: 'Props', color: '#9575CD' },
  SET_DRESSING: { label: 'Set Dressing', color: '#4FC3F7' },
  SPECIAL_EFFECTS: { label: 'Special Effects', color: '#4DB6AC' },
  VEHICLES: { label: 'Vehicles', color: '#F06292' },
  ANIMALS: { label: 'Animals', color: '#FF8A65' },
  WARDROBE: { label: 'Wardrobe', color: '#7986CB' },
  MAKEUP: { label: 'Makeup', color: '#FFD54F' },
  HAIR: { label: 'Hair', color: '#A1887F' },
  SOUND: { label: 'Sound Effects', color: '#90A4AE' },
  MUSIC: { label: 'Music', color: '#7E57C2' },
  CAMERA: { label: 'Camera Equipment', color: '#4DD0E1' },
  LIGHTING: { label: 'Lighting', color: '#DCE775' },
  GRIP: { label: 'Grip Equipment', color: '#BA68C8' },
  ELECTRICAL: { label: 'Electrical', color: '#FF8A65' },
  LOCATIONS: { label: 'Locations', color: '#4DB6AC' },
  TIME: { label: 'Time of Day', color: '#FF8C00' },
  WEATHER: { label: 'Weather', color: '#87CEEB' },
  PRODUCTION_NOTES: { label: 'Production Notes', color: '#78909C' }
};

// ─── Quick Insert Elements ─────────────────────────────────────────────────────
// Single canonical array — used by the toolbar, keyboard shortcuts, and
// snippet insertion. QUICK_INSERT_SNIPPETS below is derived from this for
// backward-compatible key-based lookups.

export const QUICK_INSERTS = [
  { key: 'scene',         snippet: '\nINT. LOCATION - DAY\n\n',                          label: 'Scene',          shortcut: 'Ctrl+Shift+H',      icon: MovieFilterIcon },
  { key: 'action',        snippet: '\nAction description here.\n\n',                     label: 'Action',         shortcut: 'Ctrl+Shift+A',      icon: EditNoteIcon },
  { key: 'character',     snippet: '\nCHARACTER NAME\n',                                 label: 'Character',      shortcut: 'Ctrl+Shift+C',      icon: PersonIcon },
  { key: 'dialogue',      snippet: 'Dialogue goes here.\n\n',                            label: 'Dialogue',       shortcut: 'Ctrl+Shift+G',      icon: RecordVoiceOverIcon },
  { key: 'parenthetical', snippet: '    (parenthetical)\n',                              label: 'Parenthetical',  shortcut: 'Ctrl+Shift+P',      icon: ChatBubbleOutlineIcon },
  { key: 'transition',    snippet: '\nCUT TO:\n\n',                                      label: 'Transition',     shortcut: 'Ctrl+Shift+T',      icon: EastIcon },
  { key: 'centered',      snippet: '\n>THE END<\n\n',                                    label: 'Centered Text',  shortcut: 'Ctrl+Shift+E',      icon: FormatAlignCenterIcon },
  { key: 'dualDialogue',  snippet: '\nCHARACTER ONE\n(\u200B)\n\u200B\n\nCHARACTER TWO ^\n(\u200B)\n\u200B\n\n', label: 'Dual Dialogue', shortcut: 'Ctrl+Shift+D', icon: PersonIcon },
  { key: 'lyric',         snippet: '\n~Lyric line here\n\n',                             label: 'Lyric',          shortcut: 'Ctrl+Shift+L',      icon: MusicNoteIcon },
  { key: 'pageBreak',     snippet: '\n===\n\n',                                          label: 'Page Break',     shortcut: 'Ctrl+Shift+Enter',  icon: HorizontalRuleIcon },
];

export const QUICK_INSERT_SNIPPETS = Object.fromEntries(
  QUICK_INSERTS.map(q => [q.key, q.snippet])
);

// ─── Dropdown Options ───────────────────────────────────────────────────────────

export const TRANSITION_OPTIONS = [
  'CUT TO:',
  'FADE IN:',
  'FADE OUT:',
  'DISSOLVE TO:',
  'SMASH CUT TO:'
];

export const TITLE_PAGE_TEMPLATE = `Title: MY SCRIPT TITLE
Credit: Written by
Author: Your Name
Source: Story by
Draft date: February 2026
Contact: your@email.com`;

export const TIME_OF_DAY_OPTIONS = [
  'DAY', 'NIGHT', 'MORNING', 'AFTERNOON', 'EVENING', 'LATE NIGHT', 'DAWN', 'DUSK', 'CONTINUOUS', 'LATER',
  'SUNRISE', 'SUNSET', 'PRE-DAWN', 'HIGH NOON', 'MIDDAY', 'LATE AFTERNOON', 'MIDNIGHT'
];

// ─── Autocomplete Regexes ───────────────────────────────────────────────────────
// Pre-compiled to avoid re-creation per keystroke.

export const AC_PREFIX_RE = /^(INT|EXT|I\/E|EST|INT\/EXT|INTERCUT|MONTAGE|SERIES OF SHOTS|FLASHBACK|POV|INSERT)\.?\s*$/i;
export const AC_LOCATION_RE = /^(INT\.|EXT\.|INT\/EXT\.|INT\.\/EXT\.|INT\.\s*EXT\.|I\/E\.|EST\.)\s+(.*)$/i;
export const AC_TIME_RE = /^(?:INT\.|EXT\.|INT\/EXT\.|INT\.\/EXT\.|INT\.\s*EXT\.|I\/E\.|EST\.)\s+.+\s+-\s*(.*)$/i;
export const AC_CHARACTER_RE = /^[A-Z][A-Z\s\-']{1,}$/;
